import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/rotate-vector-map/definition"
import {ImageOpNodeExtractChannel} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-channel/node"
import {ImageOpNodeMapZeroOneToMinusOnePlusOne} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/map-zero-one-to-minus-one-plus-one/node"
import {ImageOpNodeMath} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/math/node"
import {ImageOpNodeMapMinusOnePlusOneToZeroOne} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/map-minus-one-plus-one-to-zero-one/node"
import {ImageOpNodeCombineChannels} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/combine-channels/node"
import {deg2rad} from "@cm/math"
import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {AnyImageOpContext, ImageOpOutputs} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"

@registerNode
export class ImageOpNodeRotateVectorMap extends CompositeImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeRotateVectorMap"
    static definition = definition

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof definition>> {
        const {sourceImage, angleInDegrees, angleFactor} = await evaluator.getAll(this.parameters)

        const rawX = imageOpNodeBag.getNode("extractChannelR", ImageOpNodeExtractChannel, {
            sourceImage: sourceImage,
            channel: "R",
        })
        const rawY = imageOpNodeBag.getNode("extractChannelG", ImageOpNodeExtractChannel, {
            sourceImage: sourceImage,
            channel: "G",
        })
        const rawZ = imageOpNodeBag.getNode("extractChannelB", ImageOpNodeExtractChannel, {
            sourceImage: sourceImage,
            channel: "B",
        })

        const x = imageOpNodeBag.getNode("mapZeroOneToMinusOnePlusOneX", ImageOpNodeMapZeroOneToMinusOnePlusOne, {
            sourceImage: rawX,
        })
        const y = imageOpNodeBag.getNode("mapZeroOneToMinusOnePlusOneY", ImageOpNodeMapZeroOneToMinusOnePlusOne, {
            sourceImage: rawY,
        })

        const xTimesAngleCos = imageOpNodeBag.getNode("calcXTimesAngleCos", ImageOpNodeMath, {
            operandA: x,
            operandB: Math.cos(deg2rad(angleInDegrees * angleFactor)),
            operator: "*",
        })
        const yTimesAngleSin = imageOpNodeBag.getNode("calcYTimesAngleSin", ImageOpNodeMath, {
            operandA: y,
            operandB: Math.sin(deg2rad(angleInDegrees * angleFactor)),
            operator: "*",
        })
        const rotatedX = imageOpNodeBag.getNode("calcRotatedX", ImageOpNodeMath, {
            operandA: xTimesAngleCos,
            operandB: yTimesAngleSin,
            operator: "+",
        })

        const yTimesAngleCos = imageOpNodeBag.getNode("calcYTimesAngleCos", ImageOpNodeMath, {
            operandA: y,
            operandB: Math.cos(deg2rad(angleInDegrees * angleFactor)),
            operator: "*",
        })
        const xTimesAngleSin = imageOpNodeBag.getNode("calcXTimesAngleSin", ImageOpNodeMath, {
            operandA: x,
            operandB: Math.sin(deg2rad(angleInDegrees * angleFactor)),
            operator: "*",
        })
        const rotatedY = imageOpNodeBag.getNode("calcRotatedY", ImageOpNodeMath, {
            operandA: yTimesAngleCos,
            operandB: xTimesAngleSin,
            operator: "-",
        })

        const rescaledRotatedX = imageOpNodeBag.getNode("mapMinusOnePlusOneToZeroOneX", ImageOpNodeMapMinusOnePlusOneToZeroOne, {
            sourceImage: rotatedX,
        })
        const rescaledRotatedY = imageOpNodeBag.getNode("mapMinusOnePlusOneToZeroOneY", ImageOpNodeMapMinusOnePlusOneToZeroOne, {
            sourceImage: rotatedY,
        })

        const result = imageOpNodeBag.getNode("combineChannels", ImageOpNodeCombineChannels, {
            sourceImageR: rescaledRotatedX,
            sourceImageG: rescaledRotatedY,
            sourceImageB: rawZ,
        })

        return await evaluator.get(result)
    }
}
